/**
 * Main's Fora device calls (keyon `AddPatientInfo` / `RemovePatientInfo` /
 * `GetPatientInfo`) on the port's device routes — Trello FKJWdJMK, MZiFlfBR.
 *
 * Two of main's calls cannot be one request with a path:
 *
 *  - **RemovePatientDevice** names the meter by its Fora serial number
 *    (`meterid`), which is what the device page shows; the API addresses a
 *    device by its id. The bridge used to put the serial where the id goes, so
 *    every removal was a 404 and Fora was never told. The chart's devices are
 *    read and the one with that serial (and type) is removed; the server then
 *    removes it from Fora too.
 *  - **GetNebulizerData** named only the chart (main's SqlClass GetNeb read
 *    every type-32 row for the patient). The bridge used to ask one device's
 *    readings for a device it could not name. Sessions are their own resource
 *    now, read whole as main read them, in main's three fields.
 */

import api, { ApiError } from './client';
import { id } from './bridgeShapes';

/** The Fora data source; what the device page's "Fora" means to the API. */
export const FORA_SOURCE = 'fora_device';

/** Every page of a chart list, bounded as the bridge's allPages is. */
async function everyPage(path, query) {
  const items = [];

  for (let page = 1; page <= 200; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await api.list(path, { ...query, page, per_page: 100 });
    items.push(...(result.items || []));

    const last = result.meta && Number(result.meta.last_page);
    if (!last || page >= last) break;
  }

  return items;
}

/**
 * The body main's AddPatientInformation / AddPatientDevice become: a Fora
 * meter registered to the chart, its Fora model code as `type`. Nothing about
 * the patient travels — main sent name, sex, e-mail and an invented height
 * and weight from the browser; the server reads what Fora needs from the
 * chart, and only for a patient Fora does not have yet.
 */
export function foraDeviceBody(p) {
  const body = {
    source: FORA_SOURCE,
    type: p.metertype,
    device_identifier: p.meterid,
    external_identifier: p.meterexid,
    gateway_identifier: p.gatewayid,
  };

  // The diagnosis the page's picker names for the meter ("E11.9 - Type 2
  // diabetes …"): main wrote it with the meter's CPT codes once Fora
  // accepted the meter, and the server does the same from its queue, so it
  // is kept on the device until then. Only when one was picked.
  if (p.icd) body.icd = p.icd;

  return body;
}

/**
 * AddPatientInformation / AddPatientDevice: register the meter to the chart.
 * `{ReCode: ['00']}` means it is registered (Fora's half runs on the server's
 * queue and the device list shows how it ended).
 *
 * A refusal — a blank or unknown serial or model (422), a meter that is
 * registered elsewhere or still being removed from Fora for another chart
 * (409), no permission (403) — is answered in main's failure shape: a
 * `ReCode` that is neither '00' nor '35', and the server's own sentence in
 * `ReMsg` and `errorMessage`, which the page alerts. The bridge's generic
 * failure body has no `ReCode` at all, and the page reads `ReCode[0]` of
 * whatever it gets: the refusal threw a TypeError out of the click handler,
 * uncaught, and the person saw nothing.
 */
export async function registerForaDeviceLegacy(p) {
  const patientId = id(p && p.pid);

  if (patientId === null) {
    const message = 'That patient could not be identified.';
    return { ReCode: ['error'], ReMsg: message, errorMessage: message };
  }

  try {
    await api.post(`/patients/${patientId}/devices`, foraDeviceBody(p));

    return { ReCode: ['00'] };
  } catch (error) {
    if (error instanceof ApiError) {
      return { ReCode: [error.code || 'error'], ReMsg: error.message, errorMessage: error.message };
    }

    throw error;
  }
}

/**
 * What the device page lists: the chart's meters and the meters taken off it
 * whose removal Fora has not completed (`filter[removal]=owed`). The page
 * shows the second kind with Fora's reason and a Retry button, and the
 * bridge must find them by serial to send the removal again.
 */
export const OWED_REMOVAL = { 'filter[removal]': 'owed' };

/**
 * RemovePatientDevice: find the chart's device by serial (and type, when
 * sent) and remove it. Answers main's shape: `{}` when done, which the page
 * reads as success, or `{ReMsg, errorMessage}` with the reason.
 *
 * A meter whose removal from Fora failed is found too, and removing it sends
 * the removal again (the server refuses to re-issue the meter until Fora has
 * let go of it, so this is the way out).
 */
export async function removePatientDeviceBySerial(p) {
  const patientId = id(p && p.pid);
  const serial = p && p.meterid !== undefined && p.meterid !== null ? String(p.meterid) : '';

  if (patientId === null || serial === '') {
    const message = 'That device could not be identified.';
    return { ReMsg: message, errorMessage: message };
  }

  try {
    const devices = await everyPage(`/patients/${patientId}/devices`, OWED_REMOVAL);
    const device = devices.find((d) => String(d.serial_number) === serial
      && (!p.metertype || !d.device_type || String(d.device_type) === String(p.metertype)));

    if (!device) {
      const message = 'That device is not registered on this chart.';
      return { ReMsg: message, errorMessage: message };
    }

    await api.delete(`/patients/${patientId}/devices/${device.id}`);

    return {};
  } catch (error) {
    if (error instanceof ApiError) {
      return { ReMsg: error.message, errorMessage: error.message };
    }

    throw error;
  }
}

/**
 * GetNebulizerData: every filed session on the chart, oldest first (main's
 * query had no ORDER BY and returned insertion order; the page reverses it),
 * as main's three fields. `MValue1` is the duration in seconds.
 */
export async function nebulizerSessionsLegacy(p) {
  const patientId = id(p && (p.pid ?? p.patientid));

  if (patientId === null) return [];

  const sessions = await everyPage(`/patients/${patientId}/nebulizer-sessions`, { sort: 'measured_at' });

  return sessions.map((session) => ({
    MDateTimeUTC: session.started_at,
    MValue1: session.duration_seconds === null || session.duration_seconds === undefined ? '' : String(session.duration_seconds),
    MDeviceID: session.device_serial || '',
  }));
}
